import { useToast } from '../../context/ToastContext';
import { MAX_CSV_ROWS } from '../../utils/csvAudience';
import CountrySelect from '../CountrySelect';
import { parsedCsv } from './campaignWizardState';

const MAX_FILE_BYTES = 1024 * 1024;
const SHOWN_ERRORS = 10;

/**
 * Pasted or uploaded `name,phone` lines (read in the browser, nothing is uploaded), the country
 * for numbers without a +, and the lines that cannot be used, by line number.
 */
const CsvAudienceInput = ({ text, country, onText, onCountry }) => {
  const toast = useToast();
  const { rows, errors, tooMany } = parsedCsv(text);

  const readFile = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;
    try {
      if (file.size > MAX_FILE_BYTES) throw new Error('That file is too large. Keep it under 1 MB.');
      onText(await file.text());
    } catch (err) {
      toast.error(err.message || 'Could not read that file');
    } finally {
      input.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        One person per line as <span className="font-mono">name,phone</span>. A header line is fine. Up to{' '}
        {MAX_CSV_ROWS} people.
      </p>
      <textarea
        aria-label="Pasted list"
        value={text}
        onChange={(e) => onText(e.target.value)}
        rows={6}
        placeholder={'Asha Rao,+919876543210\nBen Cole,+14155550123'}
        className="w-full px-3 py-2 font-mono text-base border border-input rounded-lg"
      />
      <input
        type="file"
        accept=".csv,.txt,text/csv,text/plain"
        aria-label="Upload a CSV or text file"
        onChange={readFile}
        className="block w-full text-sm text-muted-foreground"
      />
      <div>
        <p className="mb-1 text-sm text-foreground/80">Country for numbers that do not start with a +</p>
        <CountrySelect value={country} onChange={onCountry} ariaLabel="Country for numbers without +" />
      </div>

      {text.trim() && (
        <p className="text-sm text-foreground/80">
          {rows.length} usable {rows.length === 1 ? 'line' : 'lines'}
          {errors.length > 0 && `, ${errors.length} to fix`}
        </p>
      )}
      {tooMany && (
        <p className="text-sm text-red-700 dark:text-red-300">
          Only {MAX_CSV_ROWS} people fit in one list. Remove the extra lines.
        </p>
      )}
      {errors.length > 0 && (
        <ul className="p-3 space-y-1 text-sm text-red-800 dark:text-red-300 border border-red-200 dark:border-red-900 rounded-lg bg-red-50 dark:bg-red-950/50">
          {errors.slice(0, SHOWN_ERRORS).map((e) => (
            <li key={e.line}>
              Line {e.line}: {e.message}
            </li>
          ))}
          {errors.length > SHOWN_ERRORS && <li>...and {errors.length - SHOWN_ERRORS} more</li>}
        </ul>
      )}
    </div>
  );
};

export default CsvAudienceInput;
